// Which structured response blocks are worth drawing (v3 `orpheusResponseBlocks.js`). Applied where
// an answer is mapped, so no surface ever receives a block that only says "nothing here".
import type { ResponseBlockDto } from '../models/orpheus.dto'

function normalized(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/**
 * A playbook result card with nothing in it: the playbook ran but found no recommendation, or the
 * workspace has no guide. It would draw an empty card under the answer that already says so.
 */
export function isEmptyPlaybookResultCard(block: ResponseBlockDto): boolean {
  if (block.type !== 'card') return false
  const data = record(block.data)
  const scalars = record(data.scalars)
  const source = normalized(scalars.source ?? data.source ?? data.source_key)
  return (
    data.schema === 'copilot.card.v1' &&
    source === 'playbook' &&
    (Number(scalars.recommendation_count) === 0 || scalars.guide_missing === true)
  )
}

/** A text block whose title and body are both just the word "playbook". */
export function isDegeneratePlaybookTextBlock(block: ResponseBlockDto): boolean {
  if (block.type !== 'text') return false
  const data = record(block.data)
  const title = normalized(data.title ?? block.title)
  const body = normalized(data.body ?? data.text ?? block.text)
  return title === 'playbook' && body === 'playbook'
}

export function isRenderableResponseBlock(block: ResponseBlockDto): boolean {
  return !isEmptyPlaybookResultCard(block) && !isDegeneratePlaybookTextBlock(block)
}

export function renderableResponseBlocks(blocks: ResponseBlockDto[]): ResponseBlockDto[] {
  return blocks.filter(isRenderableResponseBlock)
}
